import React, { useState } from 'react';
import { XMarkIcon, CreditCardIcon, ExclamationTriangleIcon } from '@heroicons/react/24/outline';
import Button from './ui/Button';
import { ordersApi } from '@/services/api';
import { formatCurrency } from '@/utils/formatters';
import toast from 'react-hot-toast';

interface CommissionPaymentModalProps {
  isOpen: boolean;
  onClose: () => void;
  order: {
    id: number;
    order_number: string;
    total_amount: number;
    platform_commission?: number;
    requires_commission_payment?: boolean;
  };
  onSuccess: () => void;
}

const CommissionPaymentModal: React.FC<CommissionPaymentModalProps> = ({
  isOpen,
  onClose,
  order,
  onSuccess,
}) => {
  const [paymentMethod, setPaymentMethod] = useState('credit_card');
  const [isProcessing, setIsProcessing] = useState(false);

  if (!isOpen) return null;

  const commissionAmount = order.platform_commission || 0;

  const handlePayment = async () => {
    if (!paymentMethod) {
      toast.error('Please select a payment method');
      return;
    }

    setIsProcessing(true);
    try {
      await ordersApi.payCommission(order.id, paymentMethod);
      toast.success('Commission paid successfully! Order confirmed and buyer contact released.');
      onSuccess();
      onClose();
    } catch (error: any) {
      const errorMessage = error?.detail || error?.message || 'Failed to process commission payment';
      toast.error(errorMessage);
    } finally {
      setIsProcessing(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex items-center justify-center min-h-screen px-4 pt-4 pb-20 text-center sm:block sm:p-0">
        {/* Background overlay */}
        <div
          className="fixed inset-0 transition-opacity bg-gray-500 bg-opacity-75"
          onClick={onClose}
        />

        {/* Modal panel */}
        <div className="inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full">
          <div className="bg-white px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center">
                <CreditCardIcon className="h-6 w-6 text-primary-600 mr-2" />
                <h3 className="text-lg font-medium text-gray-900">Pay Commission</h3>
              </div>
              <button
                onClick={onClose}
                className="text-gray-400 hover:text-gray-500"
              >
                <XMarkIcon className="h-6 w-6" />
              </button>
            </div>

            <div className="mb-6">
              <div className="bg-yellow-50 border border-yellow-200 rounded-md p-4 mb-4">
                <div className="flex">
                  <ExclamationTriangleIcon className="h-5 w-5 text-yellow-400 mr-2" />
                  <div className="text-sm text-yellow-800">
                    <p className="font-medium">Commission Payment Required</p>
                    <p className="mt-1">
                      You need to pay the commission to confirm this order and receive buyer contact information.
                    </p>
                  </div>
                </div>
              </div>

              <div className="space-y-4">
                <div>
                  <p className="text-sm text-gray-600">Order Number</p>
                  <p className="text-lg font-semibold text-gray-900">{order.order_number}</p>
                </div>

                <div>
                  <p className="text-sm text-gray-600">Order Total</p>
                  <p className="text-lg font-semibold text-gray-900">
                    {formatCurrency(Number(order.total_amount || 0))}
                  </p>
                </div>

                <div className="border-t pt-4">
                  <p className="text-sm text-gray-600 mb-2">Commission Amount</p>
                  <p className="text-2xl font-bold text-primary-600">
                    {formatCurrency(Number(commissionAmount))}
                  </p>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Payment Method
                  </label>
                  <select
                    value={paymentMethod}
                    onChange={(e) => setPaymentMethod(e.target.value)}
                    className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:ring-primary-500 focus:border-primary-500"
                  >
                    <option value="credit_card">Credit Card</option>
                    <option value="debit_card">Debit Card</option>
                    <option value="bank_transfer">Bank Transfer</option>
                    <option value="mobile_payment">Mobile Payment</option>
                  </select>
                </div>
              </div>
            </div>

            <div className="bg-gray-50 rounded-md p-4 mb-4">
              <p className="text-xs text-gray-600">
                By paying the commission, you confirm this order and will receive:
              </p>
              <ul className="mt-2 text-xs text-gray-600 list-disc list-inside space-y-1">
                <li>Buyer's contact information (phone number and address)</li>
                <li>Order confirmation status</li>
                <li>Ability to proceed with shipping</li>
              </ul>
            </div>
          </div>

          <div className="bg-gray-50 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse">
            <Button
              onClick={handlePayment}
              disabled={isProcessing}
              className="w-full sm:w-auto sm:ml-3"
            >
              {isProcessing ? 'Processing...' : `Pay ${formatCurrency(Number(commissionAmount))}`}
            </Button>
            <Button
              variant="outline"
              onClick={onClose}
              disabled={isProcessing}
              className="mt-3 sm:mt-0 w-full sm:w-auto"
            >
              Cancel
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CommissionPaymentModal;

